// Sidebar.js — Light theme navigation with collapsible groups
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, database, ref, get, onValue } from "./firebase";
import { hasMinRole, NAV_ACCESS, ROLE_LABELS } from "./permissions";

import {
  Home,
  FileText,
  Users2,
  GraduationCap,
  CalendarDays,
  LogOut,
  Trophy,
  BookOpen,
  Megaphone,
  ClipboardList,
  Clock,
  UserCheck,
  CalendarCheck,
  Tag,
  ChevronDown,
  ChevronRight,
  ShoppingBag,
  Building2,
  Film,
  CalendarClock,
  MessageSquare,
  Settings,
  LayoutGrid,
  Newspaper,
  MessageCircle,
  Tablet,
  Award,
  BarChart3,
} from "lucide-react";

/* =====================================================
   NAV STRUCTURE
   ===================================================== */

// Primary actions — always visible at top (the things touched every single day)
const PRIMARY_ITEMS = [
  { key: "dashboard",  label: "Dashboard",  icon: Home,          path: "/" },
  { key: "students",   label: "Contacts",   icon: GraduationCap, path: "/students" },
  { key: "attendance", label: "Attendance", icon: ClipboardList, path: "/attendance" },
  { key: "kiosk",      label: "Kiosk Mode", icon: Tablet,        path: "/attendance/kiosk" },
];

// Communications — collapsible group (Leads Max + Campaigns live here too — it's all follow-up/outreach)
const COMMUNICATIONS_ITEMS = [
  { key: "messages",  label: "Messages",  icon: MessageSquare, path: "/messages" },
  { key: "texting",   label: "Texting",   icon: MessageCircle, path: "/texting" },
  { key: "news",      label: "News",      icon: Newspaper,     path: "/announcements" },
  { key: "leads-max", label: "Leads Max", icon: Trophy,        path: "/leads-max", adminOnly: true },
  { key: "campaigns", label: "Campaigns", icon: Megaphone,     path: "/campaigns" },
];

const COMMUNICATIONS_HEADER = {
  key: "communications-header",
  label: "Communications",
};

// Operations — collapsible group (day-to-day running of the school, was the header-less "Business Tools" list)
const OPERATIONS_ITEMS = [
  { key: "checklists",     label: "Open / Close",   icon: ClipboardList, path: "/checklists" },
  { key: "scheduling",     label: "Timeclock",      icon: Clock,         path: "/scheduling" },
  { key: "pro-shop",       label: "Pro Shop",       icon: ShoppingBag,   path: "/pro-shop" },
  { key: "checkout-pages", label: "Checkout Pages", icon: LayoutGrid,    path: "/checkout-pages" },
  { key: "reports",        label: "Reports",        icon: BarChart3,     path: "/reports" },
];

// Classes — collapsible group (Attendance promoted out to top-level; this is schedule/curriculum content)
const CLASSES_ITEMS = [
  { key: "calendar",       label: "Calendar",       icon: CalendarDays,  path: "/calendar" },
  { key: "krav-planner",   label: "Krav Planner",   icon: BookOpen,      path: "/krav-planner" },
  { key: "tkd-planner",    label: "Taekwondo Planner", icon: BookOpen,   path: "/tkd-planner" },
  { key: "class-schedule", label: "Class Schedule", icon: CalendarClock, path: "/class-schedule" },
  { key: "private-lessons", label: "Private Lessons", icon: GraduationCap, path: "/private-lessons" },
  { key: "video-library",  label: "Video Library",  icon: Film,          path: "/video-library" },
];

// Events & Testing — collapsible group (merged former "Registrations" + "Events" — a testing or tournament IS an event)
const EVENTS_ITEMS = [
  { key: "testings",    label: "Belt Testing",  icon: FileText,      path: "/testings" },
  { key: "black-belt",  label: "Black Belt Checklists", icon: Award,  path: "/black-belt" },
  { key: "tournaments", label: "Tournaments",   icon: Trophy,        path: "/tournaments" },
  { key: "events",      label: "Event Manager", icon: CalendarCheck, path: "/events-manager" },
  { key: "rosters",     label: "Event Rosters", icon: UserCheck,     path: "/rosters" },
];

// Settings — collapsible group (role-restricted)
const SETTINGS_ITEMS = [
  { key: "business", label: "Business", icon: Building2, path: "/setup/business" },
  { key: "coupons",  label: "Coupons",  icon: Tag,       path: "/setup/coupons" },
  { key: "tags",     label: "Tags",     icon: Tag,       path: "/setup/tags" },
  { key: "import",   label: "Import Contacts", icon: Tag, path: "/setup/import" },
  { key: "ata-import", label: "ATA Member Import", icon: Tag, path: "/setup/ata-import" },
  { key: "users",    label: "Users",    icon: Users2,    path: "/users" },
  { key: "lesson-settings", label: "Private Lesson Credits", icon: GraduationCap, path: "/setup/lesson-credits" },
  { key: "portal-accounts", label: "Portal Accounts", icon: Users2, path: "/setup/portal-accounts" },
  { key: "family-migration", label: "Family Connections", icon: Users2, path: "/setup/family-connections" },
];

/* =====================================================
   COMPONENT
   ===================================================== */

export default function Sidebar({ userRole, isAdmin, isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOnPayroll, setIsOnPayroll] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const effectiveRole = userRole || (isAdmin ? "admin" : "staff");
  const roleLabel = ROLE_LABELS[effectiveRole] || "Staff";

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    get(ref(database, `users/${uid}/onPayroll`)).then(snap => {
      setIsOnPayroll(snap.val() === true);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const messagesRef = ref(database, "messages");
    const unsub = onValue(messagesRef, (snap) => {
      const val = snap.val() || {};
      let count = 0;
      Object.values(val).forEach(t => {
        if (t.lastMessageBy === "portal" && !t.adminReadAt && t.status !== "resolved") count++;
      });
      setUnreadMessages(count);
    });
    return unsub;
  }, []);

  const [openGroups, setOpenGroups] = useState(() => {
    const path = location.pathname;
    return {
      classes: CLASSES_ITEMS.some(i => path.startsWith(i.path)),
      communications: COMMUNICATIONS_ITEMS.some(i => path.startsWith(i.path)),
      events: EVENTS_ITEMS.some(i => path.startsWith(i.path)) || path.startsWith("/events-manager"),
      operations: OPERATIONS_ITEMS.some(i => path.startsWith(i.path)),
      settings: SETTINGS_ITEMS.some(i => path.startsWith(i.path) || path.startsWith("/setup")),
    };
  });

  useEffect(() => {
    const path = location.pathname;
    setOpenGroups(prev => ({
      ...prev,
      classes: CLASSES_ITEMS.some(i => path.startsWith(i.path)) ? true : prev.classes,
      communications: COMMUNICATIONS_ITEMS.some(i => path.startsWith(i.path)) ? true : prev.communications,
      events: EVENTS_ITEMS.some(i => path.startsWith(i.path)) ? true : prev.events,
      operations: OPERATIONS_ITEMS.some(i => path.startsWith(i.path)) ? true : prev.operations,
      settings: SETTINGS_ITEMS.some(i => path.startsWith(i.path) || path.startsWith("/setup")) ? true : prev.settings,
    }));
  }, [location.pathname]);

  const toggleGroup = (groupId) => {
    setOpenGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
      alert("Error signing out. Please try again.");
    }
  };

  const handleNavClick = (item) => {
    if (item.path === "/") {
      window.location.href = "/";
      return;
    }
    navigate(item.path);
    if (onClose) onClose();
  };

  const canSeeItem = (item) => {
    if (item.adminOnly && !isAdmin) return false;
    const minRole = NAV_ACCESS[item.key] || "staff";
    if (!hasMinRole(effectiveRole, minRole)) return false;
    if (item.key === "scheduling") {
      return hasMinRole(effectiveRole, "admin") || isOnPayroll;
    }
    return true;
  };

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    // Kiosk Mode has its own menu item, so don't also light up Attendance there
    if (path === "/attendance" && location.pathname.startsWith("/attendance/kiosk")) return false;
    return location.pathname.startsWith(path);
  };

  /* ---------- Render Helpers ---------- */

  const renderPrimaryButton = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    const badge = item.key === "messages" ? unreadMessages : 0;
    
    return (
      <button
        key={item.key}
        onClick={() => handleNavClick(item)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          padding: "12px 16px",
          marginBottom: 4,
          background: active ? "#2563eb" : "transparent",
          color: active ? "#fff" : "#374151",
          border: "none",
          borderRadius: 10,
          cursor: "pointer",
          fontSize: 15,
          fontWeight: active ? 600 : 500,
          transition: "all 0.15s ease",
        }}
      >
        <Icon size={20} style={{ marginRight: 12, flexShrink: 0 }} />
        <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>
        {badge > 0 && (
          <span style={{
            background: "#ef4444",
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
            minWidth: 20,
            height: 20,
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 6px",
          }}>{badge}</span>
        )}
      </button>
    );
  };

  const renderGroup = (groupId, label, items, GroupIcon) => {
    const isOpen = openGroups[groupId];
    const hasActive = items.some(i => isActive(i.path));
    const visibleItems = items.filter(canSeeItem);
    
    if (visibleItems.length === 0) return null;

    return (
      <div
        key={groupId}
        style={{
          marginBottom: 12,
          background: "#fff",
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          overflow: "hidden",
        }}
      >
        {/* Group Header */}
        <button
          onClick={() => toggleGroup(groupId)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 14px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: hasActive ? "#111827" : "#6b7280",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {GroupIcon && <GroupIcon size={18} style={{ color: hasActive ? "#2563eb" : "#9ca3af" }} />}
            <span style={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.8px",
            }}>
              {label}
            </span>
          </div>
          {isOpen
            ? <ChevronDown size={20} style={{ color: "#9ca3af" }} />
            : <ChevronRight size={20} style={{ color: "#9ca3af" }} />
          }
        </button>

        {/* Group Items */}
        {isOpen && (
          <div style={{ padding: "4px 8px 8px 8px" }}>
            {visibleItems.map(item => (
              <button
                key={item.key}
                onClick={() => handleNavClick(item)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  padding: "9px 12px 9px 34px",
                  marginBottom: 2,
                  background: isActive(item.path) ? "#eff6ff" : "transparent",
                  color: isActive(item.path) ? "#2563eb" : "#4b5563",
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: isActive(item.path) ? 500 : 400,
                  textAlign: "left",
                  position: "relative",
                  transition: "all 0.15s ease",
                }}
              >
                {isActive(item.path) && (
                  <div style={{
                    position: "absolute",
                    left: 14,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    background: "#2563eb",
                  }} />
                )}
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Special render for Communications - uses Megaphone icon, News goes to /newsletter
  const renderCommunicationsGroup = () => {
    const groupId = "communications";
    const isOpen = openGroups[groupId];
    const hasActive = COMMUNICATIONS_ITEMS.some(i => isActive(i.path));
    const visibleItems = COMMUNICATIONS_ITEMS.filter(canSeeItem);

    if (visibleItems.length === 0) return null;

    return (
      <div
        key={groupId}
        style={{
          marginBottom: 12,
          background: "#fff",
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          overflow: "hidden",
        }}
      >
        {/* Group Header - Toggle only */}
        <button
          onClick={() => toggleGroup(groupId)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 14px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: hasActive ? "#111827" : "#6b7280",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Megaphone size={18} style={{ color: hasActive ? "#2563eb" : "#9ca3af" }} />
            <span style={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.8px",
            }}>
              {COMMUNICATIONS_HEADER.label}
            </span>
          </div>
          {isOpen
            ? <ChevronDown size={20} style={{ color: "#9ca3af" }} />
            : <ChevronRight size={20} style={{ color: "#9ca3af" }} />
          }
        </button>

        {/* Group Items */}
        {isOpen && (
          <div style={{ padding: "4px 8px 8px 8px" }}>
            {visibleItems.map(item => (
              <button
                key={item.key}
                onClick={() => handleNavClick(item)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  padding: "9px 12px 9px 34px",
                  marginBottom: 2,
                  background: isActive(item.path) ? "#eff6ff" : "transparent",
                  color: isActive(item.path) ? "#2563eb" : "#4b5563",
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: isActive(item.path) ? 500 : 400,
                  textAlign: "left",
                  position: "relative",
                  transition: "all 0.15s ease",
                }}
              >
                {isActive(item.path) && (
                  <div style={{
                    position: "absolute",
                    left: 14,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    background: "#2563eb",
                  }} />
                )}
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDivider = (label = null) => (
    <div style={{ margin: "16px 0", position: "relative" }}>
      <div style={{
        height: 1,
        background: "linear-gradient(90deg, transparent, #e5e7eb, transparent)",
      }} />
      {label && (
        <span style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "#f9fafb",
          padding: "0 12px",
          fontSize: 10,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1px",
          color: "#9ca3af",
        }}>
          {label}
        </span>
      )}
    </div>
  );

  const visiblePrimary = PRIMARY_ITEMS.filter(canSeeItem);
  const visibleSettings = SETTINGS_ITEMS.filter(canSeeItem);

  return (
    <>
      <aside className={`sidebar ${isOpen ? "open" : ""}`} style={{
        background: "#f9fafb",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        padding: "16px",
        borderRight: "1px solid #e5e7eb",
      }}>

        {/* LOGO */}
        <div style={{ marginBottom: 20, textAlign: "center" }}>
          <div style={{
            background: "#fff",
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 8,
            border: "1px solid #e5e7eb",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            cursor: "pointer",
          }}
            role="button"
            title="Go to Dashboard"
            onClick={() => { window.location.href = "/"; }}
          >
            <img
              src="/ninja-wide.png"
              alt="iXL Ninja"
              style={{ width: "100%", maxWidth: 170, height: "auto", display: "block", margin: "0 auto" }}
            />
          </div>
          <div style={{
            fontSize: 10,
            color: "#6b7280",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1px",
          }}>
            {roleLabel}
          </div>
        </div>

        <nav style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          
          {/* ===== PRIMARY NAVIGATION ===== */}
          <div style={{ marginBottom: 8 }}>
            {visiblePrimary.map(item => renderPrimaryButton(item))}
          </div>

          {renderDivider()}

          {/* ===== COLLAPSIBLE GROUPS ===== */}
          {renderGroup("classes", "Classes", CLASSES_ITEMS, LayoutGrid)}
          {renderGroup("events", "Events & Testing", EVENTS_ITEMS, Trophy)}
          {renderCommunicationsGroup()}
          {renderGroup("operations", "Operations", OPERATIONS_ITEMS, Building2)}

          {visibleSettings.length > 0 && (
            <>
              {renderDivider("Admin")}
              {renderGroup("settings", "Settings", SETTINGS_ITEMS, Settings)}
            </>
          )}

        </nav>

        {/* LOGOUT */}
        <div style={{ marginTop: 8, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
          <button
            onClick={handleLogout}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              padding: "12px 16px",
              background: "#fef2f2",
              color: "#dc2626",
              border: "1px solid #fecaca",
              borderRadius: 10,
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
              transition: "all 0.15s ease",
            }}
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 998,
          }}
        />
      )}
    </>
  );
}
